import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, catchError, EMPTY, Subject, takeUntil} from "rxjs";
import {BillingTransactionModel} from "@services/back-office/model/billingTransactionModel";
import {AbstractControl, UntypedFormControl} from "@angular/forms";
import {Store} from "@ngrx/store";
import {Reducers} from "@app/reducers";
import {ConfigService} from "@config/config.service";
import {BillingService} from "@shared/services/billing.service";
import {map} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import {ValidateIfObject} from "@shared/validators/if-object.validator";
import * as billingPaymentActions from '@billing/ngrx/actions/payments.actions';
import {Actions, ofType} from "@ngrx/effects";
import { HttpErrorResponse } from "@angular/common/http";
import {SharedService} from "@shared/shared.service";
import {TranslateService} from "@ngx-translate/core";
import {ListDialogComponent} from "@shared/components/list-dialog/list-dialog.component";
import {ListItemType} from "@shared/enum/list-item-type";
import * as contractsActions from "@billing/ngrx/actions/contracts.actions";
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {NameResponse} from "@services/back-office/model/nameResponse";

/** An item picked from the list, or nothing at all — but never the free text typed into the field. */
function validateIfObjectOrEmpty(control: AbstractControl) {
    return control.value ? ValidateIfObject(control) : null;
}

@Component({
    selector: 'app-payment-dialog',
    templateUrl: './payment-dialog.component.html',
    styleUrls: ['./payment-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PaymentDialogComponent implements OnInit, OnDestroy {
    private destroyed$ = new Subject<void>();
    loading$ = new BehaviorSubject(false);
    closeDialogFlag: boolean;

    /** Optional: clearing it detaches the transaction from its contract, which is how a misassigned one is undone. */
    contract: UntypedFormControl = new UntypedFormControl('', [validateIfObjectOrEmpty]);
    /** A refund: the incoming payment it returns. Choosing one also moves the refund to that payment's contract. */
    refundOf: UntypedFormControl = new UntypedFormControl('');
    /** An incoming payment: the refunds that return it. */
    refunds: NameResponse[] = [];
    refundToAdd: UntypedFormControl = new UntypedFormControl('');

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            payment: BillingTransactionModel
        },
        private dialogRef: MatDialogRef<PaymentDialogComponent>,
        private config: ConfigService,
        private billingService: BillingService,
        private toastrService: ToastrService,
        private translateService: TranslateService,
        private store$: Store<Reducers>,
        private actions$: Actions,
        private sharedService: SharedService
    ) {
    }

    ngOnInit(): void {
        if (this.data.payment?.id) {
            this.loading$.next(true);
            this.billingService.getPayment(this.data.payment.id).pipe(
                takeUntil(this.destroyed$),
                map((res: BillingTransactionModel) => {
                    this.patchForm(res);
                    this.loading$.next(false);
                }),
                catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                        this.translateService,
                        this.toastrService,
                        `responseErrors.${error.error.message}`
                    );
                    this.closeDialog(false);
                    return EMPTY;
                })
            ).subscribe();
        }

        this.watchEditPaymentSuccess();
        this.watchEditPaymentFailure();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    patchForm(payment: BillingTransactionModel) {
        this.data.payment = payment;
        this.contract.setValue(payment.contract ? payment.contract : '');
        this.refundOf.setValue(payment.refundOf ? payment.refundOf : '');
        this.refunds = payment.refunds ? [...payment.refunds] : [];
        this.refundToAdd.reset('', {emitEvent: false});
        this.updateContractState();
    }

    /** A refund bound to an original follows that payment's contract, so the contract is not chosen by hand. */
    updateContractState() {
        if (this.data.payment.refund && this.refundOf.value?.id) {
            this.contract.disable({emitEvent: false});
        } else {
            this.contract.enable({emitEvent: false});
        }
    }

    /** A payment is «рознесений» once it is pinned to a contract; which documents it closes is derived from the balance. */
    getUnhandled(): number {
        return this.data.payment.contract?.id ? 0 : Math.abs(this.data.payment.amount ? this.data.payment.amount : 0);
    }

    /** What the refunds of this payment have already taken back. */
    getRefundedAmount(): number {
        return this.data.payment.refundedAmount ? this.data.payment.refundedAmount : 0;
    }

    addRefund() {
        const refund = this.refundToAdd.value;
        if (!refund?.id || this.refunds.some(item => item.id == refund.id)) {
            this.refundToAdd.reset('');
            return;
        }

        this.refunds = [...this.refunds, {id: refund.id, name: refund.name}];
        this.refundToAdd.reset('');
    }

    removeRefund(refundId: string) {
        this.refunds = this.refunds.filter(item => item.id != refundId);
    }

    submitForm(closeDialog?: boolean) {
        const request: BillingTransactionModel = {
            id: this.data.payment.id,
            ...!this.contract.disabled && this.contract.value && {contract: this.contract.value},
            ...this.data.payment.refund
                ? {refundOf: this.refundOf.value ? this.refundOf.value : undefined}
                : {refunds: this.refunds}
        };

        this.store$.dispatch(new billingPaymentActions.EditPayment(request));

        this.closeDialogFlag = !!closeDialog;
        this.loading$.next(true);
    }

    /** Finds the contract by the number of an act the payer referenced in the payment purpose. */
    fillByAct() {
        const listDialog$ = this.sharedService.openDialog(
            ListDialogComponent,
            {...this.config.itemsListDialog, hasBackdrop: true},
            {itemsType: ListItemType.ACT, props: {dahCounterParty: this.data.payment.dahCounterParty}}
        );

        listDialog$.afterClosed().pipe(
            takeUntil(this.destroyed$)
        ).subscribe((dialogData) => {
            if (dialogData?.data?.contract) {
                this.contract.setValue(dialogData.data.contract);
            }
        });
    }

    watchEditPaymentSuccess() {
        this.actions$.pipe(
            ofType(billingPaymentActions.PaymentsActionTypes.EditPaymentSuccess),
            takeUntil(this.destroyed$)
        ).subscribe((action: billingPaymentActions.EditPaymentSuccess) => {
            this.patchForm(action.payload);

            if (this.closeDialogFlag) {
                this.closeDialog(true);
            } else {
                this.loading$.next(false);
            }

            if (action.payload?.contract?.id) {
                this.store$.dispatch(new contractsActions.UpdateContract(action.payload.contract.id));
            }
        });
    }

    watchEditPaymentFailure() {
        this.actions$.pipe(
            ofType(billingPaymentActions.PaymentsActionTypes.EditPaymentFailure),
            takeUntil(this.destroyed$)
        ).subscribe(() => {
            this.loading$.next(false);
        });
    }

    closeDialog(result: boolean, data?: any) {
        this.dialogRef.close({result, data});
    }
}
